/**
 * TanStack Router — production-only.
 *
 * Изолированный routeTree для production.<домен>:
 *   /             → ProductionPage (Производственный календарь)
 *   /production/$projectId → ProductionDetailPage
 *   /profile, /profile/$userId → ProfilePage
 *   /login, /reset-password
 */
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { ProductionShell } from "@/components/ProductionShell";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ProductionPage } from "@/pages/ProductionPage";
import { ProductionLogPage } from "@/pages/ProductionLogPage";
import { ProductionDetailPage } from "@/pages/ProductionDetailPage";
import { SupportPage } from "@/pages/SupportPage";
import { VisitsFunnelPage } from "@/pages/VisitsFunnelPage";
import { ProductionSettingsPage } from "@/pages/ProductionSettingsPage";


interface RouterContext {
  queryClient: QueryClient;
}


function AuthGate() {
  const { user, isLoading, logout } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="muted">Проверка сессии…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // production-сабдомен доступен только пользователям с прописанным
  // "production" в departments (плюс owner/admin/superuser). Остальным —
  // экран отказа со ссылкой на главный дашборд и кнопкой выйти.
  if (!hasAccess(user, "production")) {
    return (
      <div style={{
        minHeight: "100vh", display: "grid", placeItems: "center",
        background: "#f4f4f5", padding: 24, fontFamily: "Inter Tight, -apple-system, sans-serif",
      }}>
        <div style={{
          maxWidth: 440, background: "#fff", border: "1px solid #e6e6e6",
          borderRadius: 16, padding: "28px 32px", textAlign: "center",
          boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
          <h2 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 700, color: "#222" }}>
            Доступ к производству закрыт
          </h2>
          <div style={{ fontSize: 13, color: "#707579", lineHeight: 1.5, marginBottom: 20 }}>
            Этот раздел доступен сотрудникам производства. Если доступ нужен — попросите
            администратора добавить «production» в ваши департаменты.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <a
              href={(() => {
                const h = typeof window !== "undefined" ? window.location.hostname : "";
                return `https://${h.replace(/^production\./, "")}/`;
              })()}
              style={{
                padding: "8px 16px", background: "#4fae4e", color: "#fff",
                borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none",
              }}
            >На дашборд продаж</a>
            <button
              type="button"
              onClick={() => { void logout(); }}
              style={{
                padding: "8px 16px", background: "transparent", border: "1px solid #d4d4d8",
                color: "#222", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
              }}
            >Выйти</button>
          </div>
          <div style={{ marginTop: 16, fontSize: 11, color: "#9e9e9e" }}>
            Вы вошли как {user.email}
          </div>
        </div>
      </div>
    );
  }
  return <ProductionShell />;
}


function GuestGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}


const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GuestGate,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AuthGate,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: ProductionPage,
});

const productionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production/$projectId",
  component: ProductionDetailPage,
});

const productionLogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production-log",
  component: ProductionLogPage,
});

const visitsFunnelRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/visits-funnel",
  component: VisitsFunnelPage,
});

const supportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/support",
  component: SupportPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: ProductionSettingsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

const profileViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile/$userId",
  component: ProfilePage,
});


const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    indexRoute,
    productionDetailRoute,
    productionLogRoute,
    visitsFunnelRoute,
    supportRoute,
    settingsRoute,
    profileRoute,
    profileViewRoute,
  ]),
]);


export function buildProductionRouter(queryClient: QueryClient) {
  const rawBase = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? "/";
  const basepath = rawBase === "/" ? undefined : rawBase.replace(/\/$/, "");
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    basepath,
  });
}
